import constants, { FieldName, Options, Message, Address } from './constants';
import validation from './validation';
import messages from './messages';
const addressFields = constants.fields;
const addressOptionalFields = constants.optionalFields;

function getOptionalFields(options: Options): string[] {
    const optionalFields: FieldName[] = [];
    if (!options.isCountryUS) {
        optionalFields.push(constants.FIELDS.FIELD_STATE);
    }
    if (!options.isZipCodeRequired) {
        optionalFields.push(constants.FIELDS.FIELD_ZIP_CODE);
    }
    if (!options.isPhoneRequired) {
        optionalFields.push(constants.FIELDS.FIELD_PHONE_NUMBER);
    }
    if (!options.isLabelRequired) {
        optionalFields.push(constants.FIELDS.FIELD_LABEL);
    }
    return addressOptionalFields.concat(optionalFields);
}

function getErrorMessage(
    field: FieldName | undefined,
    value: string | null | undefined,
    options: Options
): Message {
    if (!field) {
        return null;
    }
    const isRequired = !getOptionalFields(options).includes(field);
    const validationRules = validation.rules[field]?.(value || '', options) || [];
    const failedRules = value
        ? validationRules.filter(function (rule) {
              // eslint-disable-line no-extra-boolean-cast
              return !rule.isValid;
          })
        : [];

    if (failedRules.length) {
        return failedRules[0].message;
    } else if (isRequired && !(value || '').trim()) {
        return messages[field as keyof typeof messages] || messages.required;
    }
    return null;
}

/**
 * Return an Object with two properties: errors and isValid:
 * errors (Object): field name and error message (Example -> { firstName: "Required field", line1: "Item may not ship to PO Box" })
 * isValid (Boolean): check if the address has any errors
 * @param address (Object): field names and values
 * @param options (Object): can expect one of the following keys:
 * fields (Array) - custom list of fields to validate
 * allowPOBox (Boolean) - default: false
 * allowLongUSZipCode (Boolean) - default: true
 * isZipCodeRequired (Boolean) - default: true
 * isPhoneRequired (Boolean) - default: true
 */
function validate(
    _address: Address,
    _options?: Options
): { isValid: boolean; errors: Record<FieldName, Message> } {
    const address = _address || {};
    const options = {
        ...validation.defaultOptions,
        ...(_options || {}),
        isCountryUS: address.country === 'US',
    };
    const errors = (options.fields || addressFields).reduce(function (
        _errors: Record<FieldName, Message>,
        field: FieldName
    ) {
        const message = getErrorMessage(field, address[field], options);
        if (message) {
            _errors[field] = message;
        }
        return _errors;
    },
    {} as Record<FieldName, Message>);

    return {
        errors: errors,
        isValid: !Object.keys(errors).length,
    };
}

/**
 * Return an Object with property -
 * error (String or null): error message (Example -> { error: "Item may not ship to PO Box" })
 * @param value (String): field value
 * @param options (Object): can expect one of the following keys:
 * field (String) - field to validate
 * allowPOBox (Boolean) - default: false
 * isCountryUS (Boolean) - default: true
 * allowLongUSZipCode (Boolean) - default: true
 * isZipCodeRequired (Boolean) - default: true
 * isPhoneRequired (Boolean) - default: true
 */
export function validateField(value: string, _options: Options): { error: Message } {
    const options = { ...validation.defaultOptions, ...(_options || {}) };
    return {
        error: getErrorMessage(options.field, value, options) || null,
    };
}

export default validate;
